import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { AutoSizeText } from "@-/dev.improve.haniverse.components.ui.text.auto-size";
import { MorphemeMnemonicsHeroGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content._components.hero-glyph";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useMorphemeTranscription } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import {
  resolvePinyinSoundHint,
  resolvePronunciationHint,
  type PinyinSoundHint,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin.hint";
import { getGraphemeReadings } from "@-/dev.improve.haniverse.assets.words.readings";
import { useConvertedCharacter } from "@-/dev.improve.haniverse.lib.hanzi-script";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Platform, Text as RNText, View } from "@-/com.npmjs.react-native";
import type { ReactNode } from "react";

const TITLE_FONT_SIZE = 26;
const TITLE_LINE_HEIGHT = 30;

/**
 * Italic face for the sound-alike term, matching the glyph tab's italic mnemonic terms (the app
 * font has no italic, so the platform system font supplies it).
 */
const SOUND_ALIKE_ITALIC_CLASS_NAME = Platform.select({
  android: "font-[sans-serif] font-normal italic",
  ios: "font-[System] font-normal italic",
  web: "font-normal italic",
});

/**
 * The syllable's English sound-alike, the reading's counterpart to a glyph's mnemonic term: the
 * whole term italic, its phonetic `[…]` span also bold (`[shi]rt` → _**shi**rt_).
 */
function PronunciationSoundAlikeTerm({
  soundHint,
  testID,
}: {
  soundHint: PinyinSoundHint;
  testID?: string;
}) {
  const { displayTerm, phoneticSpan } = soundHint;
  const index = displayTerm.toLocaleLowerCase().indexOf(phoneticSpan.toLocaleLowerCase());
  return (
    <RNText className={SOUND_ALIKE_ITALIC_CLASS_NAME} testID={testID}>
      {index === -1 ? (
        displayTerm
      ) : (
        <>
          {displayTerm.slice(0, index)}
          <RNText className="font-bold">
            {displayTerm.slice(index, index + phoneticSpan.length)}
          </RNText>
          {displayTerm.slice(index + phoneticSpan.length)}
        </>
      )}
    </RNText>
  );
}

/** Pinyin above the glyph, beside the gloss and its pronunciation mnemonic surface. */
export function MorphemeMnemonicsPronunciationViewLayout({
  children,
  selectedPinyin,
}: {
  children: ReactNode;
  selectedPinyin: string;
}) {
  const [grapheme, pinyinList, definitionList, back] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.pinyinList,
    (c) => c.readonlySlice.data.definitionList,
    (c) => c.readonlySlice.data.back,
  );
  // Tone-colored like the reading tabs; falls back to the foreground color when tone colors are off.
  const { color: pinyinToneColor } = useMorphemeTranscription(selectedPinyin);
  const { hanziScript } = useActorUser();
  // The same hint the pronunciation review shows under its glyph, so a secondary reading (大 dài)
  // is not labelled with the main reading's gloss (big).
  const hint = resolvePronunciationHint({
    cardGloss: back,
    mainPinyin: getGraphemeReadings(hanziScript, grapheme)?.mainPinyin,
    pinyin: selectedPinyin,
    pinyinList,
  });
  const hintText = hint?.text || definitionList[0]?.entries[0] || "";
  const onlyInWords = hint?.onlyInWords ?? [];
  // Usage words are authored in simplified (or already traditional on a traditional-only card).
  const onlyInText = useConvertedCharacter(onlyInWords.join(", "), "simplified");
  const soundHint = resolvePinyinSoundHint(selectedPinyin);
  return (
    <View className="flex-row items-start gap-3" testID="pronunciation-learn-view">
      <View className="w-20 items-center gap-2.25 self-start" testID="pronunciation-glyph-column">
        <AutoSizeText
          className="w-full text-center font-bold"
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          maxLines={2}
          minimumFontScale={0.55}
        >
          <RNText
            style={pinyinToneColor === null ? undefined : { color: pinyinToneColor }}
            testID="pronunciation-panel-pinyin"
          >
            {toDiacriticPinyin(selectedPinyin)}
          </RNText>
        </AutoSizeText>
        <MorphemeMnemonicsHeroGlyph color={pinyinToneColor} />
      </View>
      <View className="min-w-0 flex-1 gap-2" testID="pronunciation-title-column">
        <AutoSizeText
          className="font-bold"
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          maxLines={2}
          minimumFontScale={0.55}
          testID="pronunciation-panel-hint"
        >
          {hintText}
        </AutoSizeText>
        {onlyInWords.length > 0 ? (
          <Text
            className="text-sm leading-5 text-muted-foreground"
            testID="pronunciation-panel-hint-only-in"
          >
            only in {onlyInText}
          </Text>
        ) : null}
        <Surface testID="pronunciation-mnemonic-surface">
          {soundHint === null ? null : (
            <SurfaceHeader className="p-2" testID="pronunciation-hint">
              <Text className="text-sm" testID="pronunciation-hint-text">
                <RNText className="font-bold">{soundHint.tonelessSyllable}</RNText>
                {" sounds like the "}
                <RNText className="font-bold">{soundHint.phoneticSpan}</RNText>
                {" in "}
                <PronunciationSoundAlikeTerm
                  soundHint={soundHint}
                  testID="pronunciation-panel-sound-hint"
                />
              </Text>
            </SurfaceHeader>
          )}
          <SurfaceContent className="gap-1 p-2">
            <Text
              className="text-[10px] leading-4 font-medium tracking-widest text-muted-foreground"
              role="heading"
              testID="pronunciation-mnemonic-label"
            >
              MNEMONIC
            </Text>
            {children}
          </SurfaceContent>
        </Surface>
      </View>
    </View>
  );
}
